import apiClient from "@apis/index";
import { useEffect, useRef } from "react";
import useLogout from "./useLogout";
import { ToastTypes, showToast } from "@components/molecules/VDBToast/util";
import { Platform } from "react-native";
import { DEVICE_SIZE, SYSTEM_PLATFORM, SYSTEM_UI } from "@utils/index";
import * as Device from "expo-device";
import i18next from "i18next";
import * as Updates from "expo-updates";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import Routes from "@apis/routes";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import NetInfo from "@react-native-community/netinfo";
import { useTheme } from "styled-components/native";
import { ResetHereToastMessage } from "@components/molecules/ResetHereToastMessage";
import { getNavigationRef } from "@navigations/navigationRef";
const useAxiosInterceptor = (navigation?: any) => {
  const handleLogout = useLogout();
  const { t } = useTranslation();
  const useUser = useUserInfo();
  const theme = useTheme();
  const currencyRef = useRef(
    useUser?.user?.currency ? useUser?.user?.currency : "USD"
  );
  const installationIdentifier = useSelector(
    (state: any) => state.appState?.installationIdentifier
  );
  const removeToastRoutes = [
    Routes.DATA_REFRESH_INFO,
    Routes.ORG_SETTINGS,
    Routes.THEME_TOKENS,
    Routes.TRANSLATIONS,
  ];

  useEffect(() => {
    currencyRef.current = useUser?.user?.currency || "USD";
  }, [useUser?.user?.currency]);

  // const fetchNetworkState = async () => {
  //   if (Platform.OS === "web") {
  //     const networkState = await NetInfo.fetch();

  //     if (!networkState.isConnected) {
  //       showToast(
  //         t("global.titleText.internetDisconnected", "Connection Error"),
  //         t(
  //           "global.text.internetDisconnected",
  //           "Please check your internet connection"
  //         ),
  //         ToastTypes.ERROR,
  //         true,
  //         true,
  //         0,
  //         () => null,
  //         false
  //       );
  //     }
  //   }
  // };

  useEffect(() => {
    const requestInterceptor = apiClient.interceptors.request.use(
      async (config) => {
        config.params = {
          ...config.params,
          device: Platform.OS,
          device_type: Platform.OS,
          system_browser: Platform.Version,
          os_version: Device?.osVersion,
          system_os: Device?.osName,
          system_screen_size: `${DEVICE_SIZE.width}x${DEVICE_SIZE.height}`,
          system_minimal_view: process.env.EXPO_PUBLIC_SYSTEM_MINIMAL_VIEW,
          system_platform: SYSTEM_PLATFORM,
          system_ui: SYSTEM_UI(theme),
          system_app_version: Updates.runtimeVersion,
          app_version: Updates.runtimeVersion,
          locale: i18next.language,
          currency: currencyRef.current,
          time_zone: "Asia/Kolkata", // To be changed
        };

        let token = await useUser.getToken();

        if (token) {
          const Token = `Token token=${token},installation_identifier=${installationIdentifier}`;
          config.headers.Authorization = Token;
        }
        return config;
      }
    );

    //fetchNetworkState();

    const resetHereToast = (message: string, username: string) => {
      let errorMessage;
      const isResetMessage =
        message &&
        (message.includes("{{reset_here}}") ||
          message.includes("reset your password"))
          ? true
          : false;
      const navigationToUse = navigation ?? getNavigationRef();
      if (isResetMessage) {
        if (navigationToUse) {
          errorMessage = (
            <ResetHereToastMessage
              message={message}
              navigation={navigationToUse}
              userInfo={useUser}
              username={username}
              t={t}
            />
          );
        } else {
          errorMessage = message.replace(
            "{{reset_here}}",
            t("login.resetHere.toastMessage.text")
          );
        }
      } else {
        errorMessage = message;
      }

      showToast(
        isResetMessage
          ? t("authorization.loginFailed.text", "Unable to sign in")
          : "Error",
        errorMessage,
        ToastTypes.ERROR,
        true,
        true,
        Platform.OS === "web" && theme.isMobile ? 100 : 40
      );
    };

    const interceptor = apiClient.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response) {
          const { status } = error.response;
          let errorMessage = error.response.data.message;
          switch (status) {
            case 401:
            case 440:
              errorMessage =
                error.response.data.message ||
                t("global.text.unauthorized", "Unauthorized!");
              if (!removeToastRoutes.includes(error.response.config.url))
                showToast("Error", errorMessage, ToastTypes.ERROR, true, true);
              handleLogout();
              break;
            case 404:
              if (errorMessage !== undefined) {
                resetHereToast(
                  errorMessage,
                  error?.config?.params?.username ?? ""
                );
                console.log(
                  "errorMessage",
                  error?.config?.params?.username ?? ""
                );
              }
              break;
            case 500:
              errorMessage = "System error. Please try again later.";
              if (!removeToastRoutes.includes(error.response.config.url))
                showToast("Error", errorMessage, ToastTypes.ERROR, true, true);
              break;

            default:
              errorMessage =
                error?.response?.data?.message &&
                typeof (error?.response?.data?.message === "string")
                  ? error?.response?.data?.message
                  : "System error. Please try again later.";
              if (
                !removeToastRoutes.includes(error.response.config.url) &&
                !error.response.config.url.includes("v3/users/password") &&
                !error.response.config.url.includes("/collections/add_items") &&
                !error.response.config.url.includes(
                  "v3/mfa_otp/validate_otp"
                ) &&
                !error.response.config.url.includes("/user_tokens")
              ) {
                showToast("Error", errorMessage, ToastTypes.ERROR, true, true);
              }
              break;
          }
        } else {
          if (error.message === "Network Error") {
            const networkState = await NetInfo.fetch();

            if (!networkState.isConnected) {
              showToast(
                t("global.titleText.internetDisconnected", "Connection Error"),
                t(
                  "global.text.internetDisconnected",
                  "Please check your internet connection"
                ),
                ToastTypes.ERROR,
                true,
                true,
                0,
                () => null,
                false
              );
            }
          }
        }

        return Promise.reject(error);
      }
    );
    return () => {
      apiClient.interceptors.request.eject(requestInterceptor);
      apiClient.interceptors.response.eject(interceptor);
    };
  });
};

export default useAxiosInterceptor;
